'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCheck } from '@fortawesome/pro-solid-svg-icons';
import { Search } from 'src/components';
import { IconButton } from 'src/components/PulseUI/IconButton';
import { useDebounce } from 'use-debounce';
import { usePathname } from 'next/navigation';

export type DropdownType = 'none' | 'companies' | 'users' | 'mappings';

export interface DropdownItem {
  label: string;
  href: string;
  isActive?: boolean;
}

interface DropdownProps {
  type: DropdownType;
  item: DropdownItem;
  items: DropdownItem[];
  isActive: boolean;
  onSelect: (href: string) => void;
  onSearchChange?: (searchTerm: string) => void;
}

const isPathActive = (
  currentPath: string,
  targetPath: string,
  type: DropdownType,
  item: DropdownItem
): boolean => {
  // If the item has an explicit isActive prop, use that
  if (typeof item.isActive !== 'undefined') {
    return item.isActive;
  }

  if (type === 'mappings') {
    // For mappings, check if the current path starts with the target path
    return currentPath.startsWith(targetPath);
  }

  // For other types, keep exact matching
  return currentPath === targetPath;
};

const Dropdown: React.FC<DropdownProps> = ({
  type,
  item,
  items,
  isActive,
  onSelect,
  onSearchChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const pathname = usePathname();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm('');
        onSearchChange?.('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onSearchChange]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  // Notify parent of search term changes for server-side search
  useEffect(() => {
    onSearchChange?.(debouncedSearchTerm);
  }, [debouncedSearchTerm, onSearchChange]);

  const filteredItems = items.filter((item) =>
    item.label.toLowerCase().includes(debouncedSearchTerm.toLowerCase().trim())
  );

  return (
    <div
      className="relative"
      ref={dropdownRef}
      data-testid={`dropdown-${type}`}
    >
      <div className="flex items-center gap-2">
        <div
          className={`flex items-center gap-2 cursor-pointer ${
            isActive
              ? 'text-sky-600 font-semibold'
              : 'text-neutral-600 hover:text-neutral-700'
          }`}
          role="button"
          tabIndex={0}
          aria-expanded={isOpen}
          aria-haspopup="true"
          data-testid={`dropdown-trigger-${type}`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setIsOpen(!isOpen);
            }
          }}
        >
          <Link
            href={`${item.href}`}
            scroll={false}
            className="text-xs transition-transform duration-200 
              ease-in-out text-link hover:text-sky-900"
          >
            {item.label}
          </Link>
          <IconButton
            icon={faCaretDown}
            variant="outlined"
            onClick={() => setIsOpen(!isOpen)}
            className={`w-5 h-5 transition-transform duration-200 ease-in-out ${
              isOpen ? '-rotate-90' : 'rotate-0'
            }`}
          />
        </div>
      </div>
      <div
        className={`absolute left-0 z-10 min-w-max 
          origin-top-left rounded-lg mt-1 bg-white shadow-lg border 
          border-neutral-200 transition-all duration-200 ease-in-out ${
            isOpen
              ? 'opacity-100 scale-100'
              : 'opacity-0 scale-95 pointer-events-none'
          }`}
        role="menu"
        data-testid={`dropdown-menu-${type}`}
      >
        <div className="p-2 border-b border-neutral-200">
          <Search
            ref={searchInputRef}
            variant="outlined"
            size="sm"
            value={searchTerm}
            onSearch={setSearchTerm}
            placeholder="Search..."
            className="w-full text-xs rounded-full"
            data-testid={`search-${type}`}
          />
        </div>
        <div className="py-2 max-h-60 overflow-y-auto flex flex-col gap-1">
          {filteredItems.map((item, index) => (
            <Link
              key={`dropdown-${type}-${index}-${item.href}`}
              href={item.href}
              scroll={false}
              className={`flex items-center justify-between px-4 py-2 text-xs transition-colors duration-200 ease-in-out w-full ${
                isPathActive(pathname, item.href, type, item)
                  ? 'bg-neutral-100 text-sky-600 font-semibold'
                  : 'text-neutral-600 hover:text-neutral-700 hover:bg-neutral-50'
              }`}
              role="menuitem"
              aria-current={
                isPathActive(pathname, item.href, type, item)
                  ? 'page'
                  : undefined
              }
              data-testid={`dropdown-item-${type}-${index}`}
              onClick={() => {
                setIsOpen(false);
                setSearchTerm('');
                onSearchChange?.('');
                onSelect(item.href);
              }}
            >
              <span className="whitespace-nowrap">{item.label}</span>
              {isPathActive(pathname, item.href, type, item) && (
                <Icon
                  icon={faCheck}
                  className="w-4 h-4 text-sky-600 ml-2"
                  aria-hidden="true"
                />
              )}
            </Link>
          ))}
          {filteredItems.length === 0 && (
            <div
              className="px-4 py-2 text-xs text-neutral-500 italic"
              data-testid={`no-results-${type}`}
            >
              No results found
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dropdown;
